// PanelStatusRail — the one global element.
//
// Two lines, sticky under the app bar. Everything here is a fact that must be
// true before any card below can be trusted, which is also the argument for
// LIVE living here and nowhere else: a freshness badge on every card would say
// the same thing twenty times and still not answer "is this board live".
//
// The flags are the only saturated things on the page. Each carries a glyph and
// a word as well as a fill, so it survives greyscale and a colour-blind reader.

import React from "react";
import styles, { C } from "./panelCard.styles";

function Flag({ tone, glyph, children, title }) {
  const toneStyle =
    tone === "alarm" ? styles.flagAlarm
      : tone === "warn" ? styles.flagWarn
        : tone === "good" ? styles.flagGood
          : null;
  return (
    <span style={{ ...styles.flag, ...toneStyle }} title={title} data-flag={tone || "calm"}>
      <span aria-hidden="true">{glyph}</span>
      {children}
    </span>
  );
}

export default function PanelStatusRail({
  facilityName,
  dateKey,
  clock,
  connection,
  counts,
  boundary,
  metrics,
  readOnly,
  locationLine,
}) {
  const { emergencies = 0, uncovered = 0, breaksWaiting = 0 } = counts || {};

  return (
    <div style={styles.rail}>
      <div style={styles.railTop}>
        <span style={styles.railTitle}>{facilityName}</span>
        <span style={styles.railMeta}>{dateKey} · {clock}</span>

        {/* Connection is a fact about the SCREEN, not about the ORs, so it sits
            apart from the alarm flags rather than among them. */}
        <span
          style={{
            ...styles.flag,
            ...(connection?.level === "live" ? styles.flagGood : styles.flagAlarm),
          }}
          data-testid="rail-connection"
          data-level={connection?.level}
        >
          <span aria-hidden="true">{connection?.glyph || "○"}</span>
          {connection?.rail || "CONNECTING…"}
        </span>

        {readOnly ? <span style={styles.flag}>READ ONLY</span> : null}
        {locationLine ? <span style={{ ...styles.railMeta, marginLeft: "auto" }}>{locationLine}</span> : null}
      </div>

      <div style={styles.railBottom}>
        <Flag
          tone={emergencies ? "alarm" : null}
          glyph="!"
          title="Live emergency alerts"
        >
          {emergencies} {emergencies === 1 ? "emergency" : "emergencies"}
        </Flag>

        <Flag tone={uncovered ? "warn" : null} glyph="∅" title="Rooms with nobody covering them">
          {uncovered} uncovered
        </Flag>

        <Flag tone={breaksWaiting ? "warn" : null} glyph="●" title="Providers waiting for a break">
          {breaksWaiting} {breaksWaiting === 1 ? "break waiting" : "breaks waiting"}
        </Flag>

        {/* Dashed border means FORECAST here, as it does on an on-call bar: this
            describes a boundary that has not happened yet and must not read as a
            live alarm. */}
        <span
          style={{
            ...styles.flag,
            borderStyle: "dashed",
            ...(boundary && boundary.delta < 0 ? styles.flagWarn : null),
          }}
          title="Staffing at the next shift boundary"
        >
          <span aria-hidden="true">▲</span>
          {boundary
            ? boundary.delta < 0
              ? `short ${Math.abs(boundary.delta)} at ${boundary.label}`
              : `ok at ${boundary.label}`
            : "no boundary ahead"}
        </span>

        <span style={{ marginLeft: "auto", display: "flex", gap: 16, color: C.muted }}>
          <span><strong style={{ color: C.ink }}>{metrics.roomsRunning}</strong> of {metrics.roomsTotal} running</span>
          <span><strong style={{ color: C.ink }}>{metrics.onShift}</strong> on shift</span>
          <span>
            <strong style={{ color: C.ink }}>{metrics.bench}</strong> free
          </span>
          {metrics.nextOut ? <span>next out {metrics.nextOut}</span> : null}
        </span>
      </div>
    </div>
  );
}
